import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { CanvasContext } from "../../../contexts/CanvasContext";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import HeaderComponent from "./HeaderComponent";
import ParagraphComponent from "./ParagraphComponent";
import IconComponent from "./IconComponent";
import LinkComponent from "./LinkComponent";
import CheckboxComponent from "./CheckboxComponent";
import ListComponent from "./ListComponent";
import RadioComponent from "./RadioComponent";
import TableComponent from "./TableComponent";
import TextInputComponent from "./TextComponent";
import VideoComponent from "./VideoComponent";
import RangeComponent from "./RangeComponent";
import PasswordInputComponent from "./PasswordInputComponent";
import EmailInputComponent from "./EmailInputComponent";
import NumberInputComponent from "./NumberInputComponent";
import DateInputComponent from "./DateInputComponent";
import ColorInputComponent from "./ColorInputComponent";
import FileInputComponent from "./FileInputComponent";
import AudioComponent from "./AudioComponent";
import DropDownComponent from "./DropDownComponent";
import MapComponent from "./MapComponent";
import Nav from "./Nav";
import HeaderTag from "./HeaderTag";
import Hgroup from "./Hgroup";
import Footer from "./Footer";
import Section from "./Section";
import Span from "./Span";
import FormComponent from "./FormComponent";
import Division from "./Division";
import AsideComponent from "./AsideComponent";
import MainComponent from "./MainComponent";
import HRTag from "./HRTag";
import BRTag from "./BRTag";
import OutputComponent from "./OutputComponent";
import IframeComponent from "./IframeComponent";

const ArticleComponent = ({ article }) => {
  const { addDivision, hoveredDivision, addElement, addCanvasDrop, files } =
    useContext(CanvasContext);
  const articleRef = useRef(null);
  const isHovered = article.id === hoveredDivision;
  const [isArticleHovered, setIsArticleHovered] = useState(false);
  const [isArticleFocused, setIsArticleFocused] = useState(false);
  const [isArticleActive, setIsArticleActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [, drop] = useDrop({
    accept: [
      "DroppableDiv",
      "DroppableButton",
      "DroppableImage",
      "DroppableHeading",
      "DroppableParagraph",
      "DroppableIcon",
      "DroppableLink",
      "DroppableCheckbox",
      "DroppableRadio",
      "DroppableList",
      "DroppableTable",
      "DroppableVideo",
      "DroppableAudio",
      "DroppableTextInput",
      "DroppableRangeInput",
      "DroppablePasswordInput",
      "DroppableEmailInput",
      "DroppableNumberInput",
      "DroppableDateInput",
      "DroppableColorInput",
      "DroppableFileInput",
      "DroppableDropDown",
      "DroppableMap",
      "DroppableNav",
      "DroppableHeader",
      "DroppableHGroup",
      "DroppableFooter",
      "DroppableSection",
      "DroppableSpan",
      "DroppableForm",
      "DroppableMain",
      "DroppableArticle",
      "DroppableAside",
      "DroppableHorizontalRule",
      "DroppableLineBreak",
      "DroppableIframe",
    ],
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        if (item.type === "DroppableDiv") {
          addDivision(article.id, item.name, item.type);
        } else if (
          item.type === "DroppableNav" ||
          item.type === "DroppableHeader" ||
          item.type === "DroppableHGroup" ||
          item.type === "DroppableFooter" ||
          item.type === "DroppableSection" ||
          item.type === "DroppableSpan" ||
          item.type === "DroppableForm" ||
          item.type === "DroppableMain" ||
          item.type === "DroppableArticle" ||
          item.type === "DroppableAside"
        ) {
          addCanvasDrop(article.id, item.name, item.type);
        } else {
          addElement(article.id, item.name, item.type);
        }
      }
    },
  });

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = article;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = article;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };

  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = article;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };

  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = article;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const generateKeyframes = () => {
    if (!article.keyframes || article.keyframes.length === 0) return null;

    return article.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          properties[processedKey] = value;
        }
      });

      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };

  const combinedRef = useMemo(
    () => (node) => {
      articleRef.current = node;
      drop(node);
    },
    [drop]
  );

  useEffect(() => {
    if (
      articleRef.current &&
      article.keyframes &&
      article.keyframes.length > 0
    ) {
      const keyframes = generateKeyframes();
      const animation = articleRef.current.animate(keyframes, {
        duration: parseFloat(article.animationDuration) * 1000 || 1000,
        delay: parseFloat(article.animationDelay) * 1000 || 0,
        iterations:
          article.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(article.animationIterationCount) || 1,
        direction: article.animationDirection || "normal",
        fill: article.animationFillMode || "none",
        easing: article.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    article.keyframes,
    article.animationDuration,
    article.animationDelay,
    article.animationIterationCount,
    article.animationDirection,
    article.animationFillMode,
    article.animationTimingFunction,
  ]);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (article.backgroundImage && article.backgroundImage.startsWith("./")) {
        const imageName = article.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(article.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [article.backgroundImage, files]);

  const focusedStyles =
    isArticleFocused && article.pseudoClass === "focus"
      ? {
          width: article.focusedwidth || article.width || "100%",
          height: article.focusedheight || article.height || "auto",

          padding: article.focusedpadding || article.padding || "8px",
          paddingTop:
            article.focusedpaddingTop || article.focusedpadding || "8px",
          paddingRight:
            article.focusedpaddingRight || article.focusedpadding || "8px",
          paddingBottom:
            article.focusedpaddingBottom || article.focusedpadding || "8px",
          paddingLeft:
            article.focusedpaddingLeft || article.focusedpadding || "8px",
          margin: article.focusedmargin || article.margin || "0px",
          marginTop: article.focusedmarginTop || article.focusedmargin || "0px",
          marginRight:
            article.focusedmarginRight || article.focusedmargin || "0px",
          marginBottom:
            article.focusedmarginBottom || article.focusedmargin || "0px",
          marginLeft:
            article.focusedmarginLeft || article.focusedmargin || "0px",

          borderTop:
            article.focusedborderTop ||
            article.focusedborder ||
            article.border ||
            "1px solid black",
          borderRight:
            article.focusedborderRight ||
            article.focusedborder ||
            article.border ||
            "1px solid black",
          borderBottom:
            article.focusedborderBottom ||
            article.focusedborder ||
            article.border ||
            "1px solid black",
          borderLeft:
            article.focusedborderLeft ||
            article.focusedborder ||
            article.border ||
            "1px solid black",
          borderSapcing:
            article.focusedborderSpacing || article.borderSpacing || "0px",
          borderCollapse:
            article.focusedborderCollapse || article.borderCollapse || "0px",
          borderRadius:
            article.focusedborderRadius || article.borderRadius || "0",
          outline: article.focusedoutline || article.outline || "none",

          position: article.focusedposition || article.position || "relative",
          top: article.focusedtop || article.top || "auto",
          right: article.focusedright || article.right || "auto",
          bottom: article.focusedbottom || article.bottom || "auto",
          left: article.focusedleft || article.left || "auto",
          zIndex: article.focusedzIndex || article.zIndex || "auto",
          boxShadow: article.focusedboxShadow || article.boxShadow || "none",
          opacity: article.focusedOpacity || article.opacity || 1,
          display: article.focuseddisplay || article.display || "block",
          flexDirection:
            article.focusedflexDirection || article.flexDirection || "column",
          flexWrap: article.focusedflexWrap || "nowrap",
          order: article.focusedorder || "0",
          flexGrow: article.focusedflexGrow || "0",
          flexShrink: article.focusedflexShrink || "1",
          flexBasis: article.focusedflexBasis || "auto",

          gridTemplateColumns: article.focusedgridTemplateColumns || "none",
          gridTemplateRows: article.focusedgridTemplateRows || "none",
          gridArea: article.focusedgridArea || "auto",
          gridAutoFlow: article.focusedgridAutoFlow || "row",
          gridAutoColumns: article.focusedgridAutoColumns || "auto",
          gridAutoRows: article.focusedgridAutoRows || "auto",
          columnGap: article.focusedcolumnGap || "0",
          rowGap: article.focusedrowGap || "0",

          justifyContent: article.focusedjustifyContent || "flex-start",
          alignItems: article.focusedalignItems || "stretch",
          alignContent: article.focusedalignContent || "stretch",
          alignSelf: article.focusedalignSelf || "auto",
          // placeContent: article.focusedplaceContent || "normal",
          // placeItems: article.focusedplaceItems || "normal",
          justifyItems: article.focusedjustifyItems || "stretch",
          justifySelf: article.focusedjustifySelf || "auto",

          gap: article.focusedgap || "0",
          visibility: article.focusedvisibility || "visible",

          color: article.focusedcolor || article.color || "#000000",
          backgroundColor:
            article.focusedbackgroundColor ||
            article.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (article.focusedbackgroundImage) {
              if (article.focusedbackgroundImage.startsWith("./")) {
                const imageName = article.focusedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${article.focusedbackgroundImage})`;
            }
            if (article.backgroundImage) {
              if (article.backgroundImage.startsWith("./")) {
                const imageName = article.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${article.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            article.focusedbackgroundPosition ||
            article.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            article.focusedbackgroundSize || article.backgroundSize || "auto",
          backgroundRepeat:
            article.focusedbackgroundRepeat ||
            article.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            article.focusedbackgroundAttachment ||
            article.backgroundAttachment ||
            "scroll",

          fontFamily:
            article.focusedfontFamily || article.fontFamily || "inherit",
          fontSize: article.focusedfontSize || article.fontSize || "inherit",
          fontWeight:
            article.focusedfontWeight || article.fontWeight || "normal",
          fontStyle: article.focusedfontStyle || article.fontStyle || "normal",
          fontVariant:
            article.focusedfontVariant || article.fontVariant || "normal",
          lineHeight:
            article.focusedlineHeight || article.lineHeight || "normal",
          textShadow: article.focusedtextShadow || article.textShadow || "none",
          textAlign: article.focusedtextAlign || article.textAlign || "left",
          verticalAlign:
            article.focusedverticalAlign || article.verticalAlign || "baseline",
          textDecoration:
            article.focusedtextDecoration || article.textDecoration || "none",
          textTransform:
            article.focusedtextTransform || article.textTransform || "none",
          letterSpacing:
            article.focusedletterSpacing || article.letterSpacing || "normal",
          wordSpacing:
            article.focusedwordSpacing || article.wordSpacing || "normal",
          textIndent: article.focusedtextIndent || article.textIndent || "0",
          whiteSpace:
            article.focusedwhiteSpace || article.whiteSpace || "normal",
          textOverflow:
            article.focusedtextOverflow || article.textOverflow || "clip",
          direction: article.focuseddirection || article.direction || "ltr",
          unicodeBidi:
            article.focusedunicodeBidi || article.unicodeBidi || "normal",
          writingMode:
            article.focusedwritingMode ||
            article.writingMode ||
            "horizontal-tb",

          cursor: article.focusedcursor || article.cursor || "default",
          filter: article.focusedfilter || article.filter || "none",
          backdropFilter:
            article.focusedbackdropFilter || article.backdropFilter || "none",
          mixBlendMode:
            article.focusedmixBlendMode || article.mixBlendMode || "normal",
          aspectRatio:
            article.focusedaspectRatio || article.aspectRatio || "auto",

          transform: article.focusedtransform || article.transform || "none",
          transition: article.focusedtransition || article.transition || "none",
        }
      : {};

  const hoverStyles =
    isArticleHovered && article.pseudoClass === "hover"
      ? {
          width: article.hoveredwidth || article.width || "100%",
          height: article.hoveredheight || article.height || "auto",

          padding: article.hoveredpadding || article.padding || "8px",
          paddingTop:
            article.hoveredpaddingTop || article.hoveredpadding || "8px",
          paddingRight:
            article.hoveredpaddingRight || article.hoveredpadding || "8px",
          paddingBottom:
            article.hoveredpaddingBottom || article.hoveredpadding || "8px",
          paddingLeft:
            article.hoveredpaddingLeft || article.hoveredpadding || "8px",
          margin: article.hoveredmargin || article.margin || "0px",
          marginTop: article.hoveredmarginTop || article.hoveredmargin || "0px",
          marginRight:
            article.hoveredmarginRight || article.hoveredmargin || "0px",
          marginBottom:
            article.hoveredmarginBottom || article.hoveredmargin || "0px",
          marginLeft:
            article.hoveredmarginLeft || article.hoveredmargin || "0px",

          borderTop:
            article.hoveredborderTop ||
            article.hoveredborder ||
            article.border ||
            "1px solid black",
          borderRight:
            article.hoveredborderRight ||
            article.hoveredborder ||
            article.border ||
            "1px solid black",
          borderBottom:
            article.hoveredborderBottom ||
            article.hoveredborder ||
            article.border ||
            "1px solid black",
          borderLeft:
            article.hoveredborderLeft ||
            article.hoveredborder ||
            article.border ||
            "1px solid black",
          borderSapcing:
            article.hoveredborderSpacing || article.borderSpacing || "0px",
          borderCollapse:
            article.hoveredborderCollapse || article.borderCollapse || "0px",
          borderRadius:
            article.hoveredborderRadius || article.borderRadius || "0",
          outline: article.hoveredoutline || article.outline || "none",

          position: article.hoveredposition || article.position || "relative",
          top: article.hoveredtop || article.top || "auto",
          right: article.hoveredright || article.right || "auto",
          bottom: article.hoveredbottom || article.bottom || "auto",
          left: article.hoveredleft || article.left || "auto",
          zIndex: article.hoveredzIndex || article.zIndex || "auto",
          boxShadow: article.hoveredboxShadow || article.boxShadow || "none",
          opacity: article.hoveredOpacity || article.opacity || 1,
          display: article.hovereddisplay || article.display || "block",
          flexDirection:
            article.hoveredflexDirection || article.flexDirection || "column",
          flexWrap: article.hoveredflexWrap || "nowrap",
          order: article.hoveredorder || "0",
          flexGrow: article.hoveredflexGrow || "0",
          flexShrink: article.hoveredflexShrink || "1",
          flexBasis: article.hoveredflexBasis || "auto",

          gridTemplateColumns: article.hoveredgridTemplateColumns || "none",
          gridTemplateRows: article.hoveredgridTemplateRows || "none",
          gridArea: article.hoveredgridArea || "auto",
          gridAutoFlow: article.hoveredgridAutoFlow || "row",
          gridAutoColumns: article.hoveredgridAutoColumns || "auto",
          gridAutoRows: article.hoveredgridAutoRows || "auto",
          columnGap: article.hoveredcolumnGap || "0",
          rowGap: article.hoveredrowGap || "0",

          justifyContent: article.hoveredjustifyContent || "flex-start",
          alignItems: article.hoveredalignItems || "stretch",
          alignContent: article.hoveredalignContent || "stretch",
          alignSelf: article.hoveredalignSelf || "auto",
          // placeContent: article.hoveredplaceContent || "normal",
          // placeItems: article.hoveredplaceItems || "normal",
          justifyItems: article.hoveredjustifyItems || "stretch",
          justifySelf: article.hoveredjustifySelf || "auto",

          gap: article.hoveredgap || "0",
          visibility: article.hoveredvisibility || "visible",

          color: article.hoveredcolor || article.color || "#000000",
          backgroundColor:
            article.hoveredbackgroundColor ||
            article.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (article.hoveredbackgroundImage) {
              if (article.hoveredbackgroundImage.startsWith("./")) {
                const imageName = article.hoveredbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${article.hoveredbackgroundImage})`;
            }
            if (article.backgroundImage) {
              if (article.backgroundImage.startsWith("./")) {
                const imageName = article.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${article.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            article.hoveredbackgroundPosition ||
            article.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            article.hoveredbackgroundSize || article.backgroundSize || "auto",
          backgroundRepeat:
            article.hoveredbackgroundRepeat ||
            article.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            article.hoveredbackgroundAttachment ||
            article.backgroundAttachment ||
            "scroll",

          fontFamily:
            article.hoveredfontFamily || article.fontFamily || "inherit",
          fontSize: article.hoveredfontSize || article.fontSize || "inherit",
          fontWeight:
            article.hoveredfontWeight || article.fontWeight || "normal",
          fontStyle: article.hoveredfontStyle || article.fontStyle || "normal",
          fontVariant:
            article.hoveredfontVariant || article.fontVariant || "normal",
          lineHeight:
            article.hoveredlineHeight || article.lineHeight || "normal",
          textShadow: article.hoveredtextShadow || article.textShadow || "none",
          textAlign: article.hoveredtextAlign || article.textAlign || "left",
          verticalAlign:
            article.hoveredverticalAlign || article.verticalAlign || "baseline",
          textDecoration:
            article.hoveredtextDecoration || article.textDecoration || "none",
          textTransform:
            article.hoveredtextTransform || article.textTransform || "none",
          letterSpacing:
            article.hoveredletterSpacing || article.letterSpacing || "normal",
          wordSpacing:
            article.hoveredwordSpacing || article.wordSpacing || "normal",
          textIndent: article.hoveredtextIndent || article.textIndent || "0",
          whiteSpace:
            article.hoveredwhiteSpace || article.whiteSpace || "normal",
          textOverflow:
            article.hoveredtextOverflow || article.textOverflow || "clip",
          direction: article.hovereddirection || article.direction || "ltr",
          unicodeBidi:
            article.hoveredunicodeBidi || article.unicodeBidi || "normal",
          writingMode:
            article.hoveredwritingMode ||
            article.writingMode ||
            "horizontal-tb",

          cursor: article.hoveredcursor || article.cursor || "default",
          filter: article.hoveredfilter || article.filter || "none",
          backdropFilter:
            article.hoveredbackdropFilter || article.backdropFilter || "none",
          mixBlendMode:
            article.hoveredmixBlendMode || article.mixBlendMode || "normal",
          aspectRatio:
            article.hoveredaspectRatio || article.aspectRatio || "auto",

          transform: article.hoveredtransform || article.transform || "none",
          transition: article.hoveredtransition || article.transition || "none",
        }
      : {};

  const activeStyles =
    isArticleActive && article.pseudoClass === "active"
      ? {
          width: article.activatedwidth || article.width || "100%",
          height: article.hoveredheight || article.height || "auto",

          padding: article.activatedpadding || article.padding || "8px",
          paddingTop:
            article.activatedpaddingTop || article.activatedpadding || "8px",
          paddingRight:
            article.activatedpaddingRight || article.activatedpadding || "8px",
          paddingBottom:
            article.activatedpaddingBottom || article.activatedpadding || "8px",
          paddingLeft:
            article.activatedpaddingLeft || article.activatedpadding || "8px",
          margin: article.activatedmargin || article.margin || "0px",
          marginTop:
            article.activatedmarginTop || article.activatedmargin || "0px",
          marginRight:
            article.activatedmarginRight || article.activatedmargin || "0px",
          marginBottom:
            article.activatedmarginBottom || article.activatedmargin || "0px",
          marginLeft:
            article.activatedmarginLeft || article.activatedmargin || "0px",

          borderTop:
            article.activatedborderTop ||
            article.activatedborder ||
            article.border ||
            "1px solid black",
          borderRight:
            article.activatedborderRight ||
            article.activatedborder ||
            article.border ||
            "1px solid black",
          borderBottom:
            article.activatedborderBottom ||
            article.activatedborder ||
            article.border ||
            "1px solid black",
          borderLeft:
            article.activatedborderLeft ||
            article.activatedborder ||
            article.border ||
            "1px solid black",
          borderSapcing:
            article.activatedborderSpacing || article.borderSpacing || "0px",
          borderCollapse:
            article.activatedborderCollapse || article.borderCollapse || "0px",
          borderRadius:
            article.activatedborderRadius || article.borderRadius || "0",
          outline: article.activatedoutline || article.outline || "none",

          position: article.activatedposition || article.position || "relative",
          top: article.activatedtop || article.top || "auto",
          right: article.activatedright || article.right || "auto",
          bottom: article.activatedbottom || article.bottom || "auto",
          left: article.activatedleft || article.left || "auto",
          zIndex: article.activatedzIndex || article.zIndex || "auto",

          boxShadow: article.activatedboxShadow || article.boxShadow || "none",
          opacity: article.activatedOpacity || article.opacity || 1,
          display: article.activateddisplay || article.display || "block",
          flexDirection:
            article.activatedflexDirection || article.flexDirection || "column",
          flexWrap: article.activatedflexWrap || "nowrap",
          order: article.activatedorder || "0",
          flexGrow: article.activatedflexGrow || "0",
          flexShrink: article.activatedflexShrink || "1",
          flexBasis: article.activatedflexBasis || "auto",

          gridTemplateColumns: article.activatedgridTemplateColumns || "none",
          gridTemplateRows: article.activatedgridTemplateRows || "none",
          gridArea: article.activatedgridArea || "auto",
          gridAutoFlow: article.activatedgridAutoFlow || "row",
          gridAutoColumns: article.activatedgridAutoColumns || "auto",
          gridAutoRows: article.activatedgridAutoRows || "auto",
          columnGap: article.activatedcolumnGap || "0",
          rowGap: article.activatedrowGap || "0",

          justifyContent: article.activatedjustifyContent || "flex-start",
          alignItems: article.activatedalignItems || "stretch",
          alignContent: article.activatedalignContent || "stretch",
          alignSelf: article.activatedalignSelf || "auto",
          // placeContent: article.activatedplaceContent || "normal",
          // placeItems: article.activatedplaceItems || "normal",
          justifyItems: article.activatedjustifyItems || "stretch",
          justifySelf: article.activatedjustifySelf || "auto",

          gap: article.activatedgap || "0",
          visibility: article.activatedvisibility || "visible",

          color: article.activatedcolor || article.color || "#000000",
          backgroundColor:
            article.activatedbackgroundColor ||
            article.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (article.activatedbackgroundImage) {
              if (article.activatedbackgroundImage.startsWith("./")) {
                const imageName = article.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${article.activatedbackgroundImage})`;
            }
            if (article.backgroundImage) {
              if (article.backgroundImage.startsWith("./")) {
                const imageName = article.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${article.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            article.activatedbackgroundPosition ||
            article.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            article.activatedbackgroundSize || article.backgroundSize || "auto",
          backgroundRepeat:
            article.activatedbackgroundRepeat ||
            article.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            article.activatedbackgroundAttachment ||
            article.backgroundAttachment ||
            "scroll",

          fontFamily:
            article.activatedfontFamily || article.fontFamily || "inherit",
          fontSize: article.activatedfontSize || article.fontSize || "inherit",
          fontWeight:
            article.activatedfontWeight || article.fontWeight || "normal",
          fontStyle:
            article.activatedfontStyle || article.fontStyle || "normal",
          fontVariant:
            article.activatedfontVariant || article.fontVariant || "normal",
          lineHeight:
            article.activatedlineHeight || article.lineHeight || "normal",
          textShadow:
            article.activatedtextShadow || article.textShadow || "none",
          textAlign: article.activatedtextAlign || article.textAlign || "left",
          verticalAlign:
            article.activatedverticalAlign ||
            article.verticalAlign ||
            "baseline",
          textDecoration:
            article.activatedtextDecoration || article.textDecoration || "none",
          textTransform:
            article.activatedtextTransform || article.textTransform || "none",
          letterSpacing:
            article.activatedletterSpacing || article.letterSpacing || "normal",
          wordSpacing:
            article.activatedwordSpacing || article.wordSpacing || "normal",
          textIndent: article.activatedtextIndent || article.textIndent || "0",
          whiteSpace:
            article.activatedwhiteSpace || article.whiteSpace || "normal",
          textOverflow:
            article.activatedtextOverflow || article.textOverflow || "clip",
          direction: article.activateddirection || article.direction || "ltr",
          unicodeBidi:
            article.activatedunicodeBidi || article.unicodeBidi || "normal",
          writingMode:
            article.activatedwritingMode ||
            article.writingMode ||
            "horizontal-tb",

          cursor: article.activatedcursor || article.cursor || "default",
          filter: article.activatedfilter || article.filter || "none",
          backdropFilter:
            article.activatedbackdropFilter || article.backdropFilter || "none",
          mixBlendMode:
            article.activatedmixBlendMode || article.mixBlendMode || "normal",
          aspectRatio:
            article.activatedaspectRatio || article.aspectRatio || "auto",

          transform: article.activatedtransform || article.transform || "none",
          transition:
            article.activatedtransition || article.transition || "none",
        }
      : {};

  const articleStyle = {
    width: article.width || "100%",
    height: article.height || "auto",
    padding: article.padding || "8px",
    paddingTop: article.paddingTop || article.padding || "8px",
    paddingRight: article.paddingRight || article.padding || "8px",
    paddingBottom: article.paddingBottom || article.padding || "8px",
    paddingLeft: article.paddingLeft || article.padding || "8px",
    margin: article.margin || "0px",
    marginTop: article.marginTop || article.margin || "0px",
    marginRight: article.marginRight || article.margin || "0px",
    marginBottom: article.marginBottom || article.margin || "0px",
    marginLeft: article.marginLeft || article.margin || "0px",

    borderTop: article.borderTop || article.border || "1px solid #000000",
    borderRight: article.borderRight || article.border || "1px solid #000000",
    borderBottom: article.borderBottom || article.border || "1px solid #000000",
    borderLeft: article.borderLeft || article.border || "1px solid #000000",

    borderSapcing: article.borderSpacing || "0px",
    borderCollapse: article.borderCollapse || "0px",
    borderRadius: article.borderRadius || "0",
    boxSizing: article.boxSizing || "border-box",

    position: article.position || "relative",
    top: article.top || "auto",
    right: article.right || "auto",
    bottom: article.bottom || "auto",
    left: article.left || "auto",
    zIndex: article.zIndex || "auto",

    display: article.display || "block",
    flexDirection: article.flexDirection || "column",
    flexWrap: article.flexWrap || "nowrap",
    order: article.order || "0",
    flexGrow: article.flexGrow || "0",
    flexShrink: article.flexShrink || "1",
    flexBasis: article.flexBasis || "auto",

    gridTemplateColumns: article.gridTemplateColumns || "none",
    gridTemplateRows: article.gridTemplateRows || "none",
    gridArea: article.gridArea || "auto",
    gridAutoFlow: article.gridAutoFlow || "row",
    gridAutoColumns: article.gridAutoColumns || "auto",
    gridAutoRows: article.gridAutoRows || "auto",
    columnGap: article.columnGap || "0",
    rowGap: article.rowGap || "0",

    justifyContent: article.justifyContent || "flex-start",
    alignItems: article.alignItems || "stretch",
    alignContent: article.alignContent || "stretch",
    alignSelf: article.alignSelf || "auto",
    // placeContent: article.placeContent || "normal",
    // placeItems: article.placeItems || "normal",
    justifyItems: article.justifyItems || "stretch",
    justifySelf: article.justifySelf || "auto",

    gap: article.gap || "0",
    visibility: article.visibility || "visible",

    color: article.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),

    backgroundPosition: article.backgroundPosition || "0% 0%",
    backgroundSize: article.backgroundSize || "auto",
    backgroundRepeat: article.backgroundRepeat || "repeat",
    backgroundAttachment: article.backgroundAttachment || "scroll",
    boxShadow: article.boxShadow || "none",
    filter: article.filter || "none",
    backdropFilter: article.backdropFilter || "none",
    mixBlendMode: article.mixBlendMode || "normal",
    aspectRatio: article.aspectRatio || "auto",

    resize: article.resize || "none",
    userSelect: article.userSelect || "auto",
    overflow: article.resize && article.resize !== "none" ? "auto" : "visible",
    overflowX: article.overflowX || "hidden",
    overflowY: article.overflowY || "hidden",

    fontSize: article.fontSize || "inherit",
    fontFamily: article.fontFamily || "inherit",
    fontWeight: article.fontWeight || "normal",
    fontStyle: article.fontStyle || "normal",
    fontVariant: article.fontVariant || "normal",
    lineHeight: article.lineHeight || "normal",
    textAlign: article.textAlign || "left",
    textDecoration: article.textDecoration || "none",
    textTransform: article.textTransform || "none",
    textShadow: article.textShadow || "none",
    verticalAlign: article.verticalAlign || "baseline",
    letterSpacing: article.letterSpacing || "normal",
    wordSpacing: article.wordSpacing || "normal",
    textIndent: article.textIndent || "0",
    whiteSpace: article.whiteSpace || "normal",
    textOverflow: article.textOverflow || "clip",
    direction: article.direction || "ltr",
    unicodeBidi: article.unicodeBidi || "normal",
    writingMode: article.writingMode || "horizontal-tb",
    cursor: article.cursor || "default",
    outline: article.outline || "none",

    backgroundColor: isHovered
      ? "#ADD8E6"
      : article.backgroundColor || "#ffffff",

    listStyleType: article.listStyleType || "disc",
    listStyleImage: article.listStyleImage
      ? `url(${article.listStyleImage})`
      : "none",
    listStylePosition: article.listStylePosition || "outside",

    transform: article.transform || "none",
    transition:
      article.transition || "transform 0.3s ease, background-color 0.3s ease",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  return (
    <div
      className="Main_Division"
      ref={combinedRef}
      style={articleStyle}
      onMouseEnter={() => setIsArticleHovered(true)}
      onMouseLeave={() => setIsArticleHovered(false)}
      onFocus={() => setIsArticleFocused(true)}
      onBlur={() => setIsArticleFocused(false)}
      onMouseDown={() => setIsArticleActive(true)}
      onMouseUp={() => setIsArticleActive(false)}
      tabIndex={0}
    >
      {article.children &&
        article.children.map((child) => {
          switch (child.name) {
            case "Button":
              return <ButtonComponent key={child.id} button={child} />;
            case "Image":
              return <ImageComponent key={child.id} image={child} />;
            case "Heading":
              return <HeaderComponent key={child.id} header={child} />;
            case "Paragraph":
              return <ParagraphComponent key={child.id} paragraph={child} />;
            case "Icon":
              return <IconComponent key={child.id} icon={child} />;
            case "Link":
              return <LinkComponent key={child.id} link={child} />;
            case "Checkbox":
              return <CheckboxComponent key={child.id} checkbox={child} />;
            case "Radio":
              return <RadioComponent key={child.id} radio={child} />;
            case "List":
              return <ListComponent key={child.id} list={child} />;
            case "Table":
              return <TableComponent key={child.id} table={child} />;
            case "Video":
              return <VideoComponent key={child.id} video={child} />;
            case "Text":
              return <TextInputComponent key={child.id} textInput={child} />;
            case "Range":
              return <RangeComponent key={child.id} range={child} />;
            case "Password":
              return (
                <PasswordInputComponent key={child.id} passwordInput={child} />
              );
            case "Email":
              return <EmailInputComponent key={child.id} emailInput={child} />;
            case "Number":
              return (
                <NumberInputComponent key={child.id} numberInput={child} />
              );
            case "Date":
              return <DateInputComponent key={child.id} dateInput={child} />;
            case "Color":
              return <ColorInputComponent key={child.id} colorInput={child} />;
            case "File":
              return <FileInputComponent key={child.id} fileInput={child} />;
            case "Audio":
              return <AudioComponent key={child.id} audio={child} />;
            case "DropDown":
              return <DropDownComponent key={child.id} dropdown={child} />;
            case "Output":
              return <OutputComponent key={child.id} output={child} />;
            case "Map":
              return <MapComponent key={child.id} map={child} />;
            case "Nav":
              return <Nav key={child.id} nav={child} />;
            case "Header":
              return <HeaderTag key={child.id} headertag={child} />;
            case "HGroup":
              return <Hgroup key={child.id} hgroup={child} />;
            case "Footer":
              return <Footer key={child.id} footer={child} />;
            case "Section":
              return <Section key={child.id} section={child} />;
            case "Span":
              return <Span key={child.id} span={child} />;
            case "Form":
              return <FormComponent key={child.id} form={child} />;
            case "Article":
              return <ArticleComponent key={child.id} article={child} />;
            case "Aside":
              return <AsideComponent key={child.id} aside={child} />;
            case "Main":
              return <MainComponent key={child.id} main={child} />;
            case "HorizontalRule":
              return <HRTag key={child.id} div={child} />;
            case "LineBreak":
              return <BRTag key={child.id} div={child} />;
            case "Iframe":
              return <IframeComponent key={child.id} div={child} />;
            default:
              return <Division key={child.id} div={child} />;
          }
        })}
    </div>
  );
};

export default ArticleComponent;
